/**
 * Installation Details List with Infinite Scroll
 *
 * Features:
 * - Infinite scroll with pagination
 * - Filters: From/To Date, InCharge, AMC Month, AMC/Non AMC, Status, Client Name, Area, City, Org Type
 * - All dropdown options are dynamic
 * - Manual fetch with filters
 */

import React, { useState, useMemo } from 'react';
import { Button, TextField, MenuItem, Box, Typography, Autocomplete } from '@mui/material';
import { Plus, Search as SearchIcon, BrushCleaning, RefreshCw } from 'lucide-react';
import DataTable from '../common/DataTable/index';
import CommonDialog from '../common/CommonDialog';
import AddInstallation from './AddInstallation';
import { fetchInstallations } from '../../../apis/InstallationDetails';
import Tooltip from '../../../utils/Tooltip';
import DatePicker from '../common/DatePicker/DatePicker';
import {
  FilterFieldSx,
  DropDownSX,
  CSS_PRIMARY,
  PRIMARY_COLOR_HOVER,
  DropDownSXWithColor,
  DatePickerSX
} from '../../../constants/style';
import { fetchCityListCombo } from '../../../apis/Masters/City';
import { fetchUsersCombo } from '../../../apis/Masters/Users';
import { fetchAreaListCombo } from '../../../apis/Masters/Area';
import { MastersId, ClientStatusOptions } from '../../../constants/constant';
import { fetchGlobalTypesCombo } from '../../../apis/Masters/GlobalType';
import { useResponsive, userScreenAccess, IsAdmins } from '../../../utils';
import Search from '../common/Search';
import ExportExcel from '../common/ExportExcel';
import dayjs from 'dayjs';
import { fetchClients } from '../../../apis/Clients';
import { localStorageKeyName } from '../../../constants/constant';
import ChooseClient from '../common/ChooseClient';
import { formatDate } from '../../../utils';

const headers = [
  {
    label: 'Client Name',
    key: 'clientName',
    minWidth: 120,
    sortable: true,
    align: 'left',
    resizable: true
  },
  {
    label: 'Area',
    key: 'area',
    minWidth: 120,
    sortable: true,
    resizable: true
  },
  {
    label: 'City',
    key: 'city',
    minWidth: 120,
    sortable: true,
    resizable: true
  },
  {
    label: 'Org Type',
    key: 'orgType',
    minWidth: 120,
    sortable: true,
    resizable: true
  },
  {
    label: 'Inst. Date',
    key: 'installationDateString',
    minWidth: 120,
    sortable: true,
    resizable: true
  },
  {
    label: 'Bill Amount',
    key: 'billAmount',
    minWidth: 120,
    sortable: true,
    resizable: true
  },
  {
    label: 'AMC Amount',
    key: 'amcAmount',
    minWidth: 120,
    sortable: true,
    resizable: true
  },
  {
    label: 'AMC End Date',
    key: 'amcEndDateString',
    minWidth: 120,
    sortable: true,
    resizable: true
  },
  {
    label: 'Days',
    key: 'days',
    minWidth: 120,
    sortable: true,
    resizable: true
  },
  {
    label: 'In Charge',
    key: 'userName',
    minWidth: 140,
    sortable: true,
    resizable: true
  },
  {
    label: 'Status',
    key: 'isActive',
    minWidth: 140,
    sortable: true,
    render: (val) => {
      const statusText = val ? 'Active' : 'Inactive';
      const badgeClass =
        {
          true: 'bg-success-subtle text-success',
          false: 'bg-danger-subtle text-danger'
        }[val] || 'bg-secondary';
      return <span className={`${badgeClass} px-2 py-1 fw-semibold`}>{statusText}</span>;
    }
  },
  {
    label: 'Version',
    key: 'versionNo',
    minWidth: 140,
    sortable: true,
    resizable: true
  },
  {
    label: 'Modules',
    key: 'modulesName',
    minWidth: 140,
    sortable: true,
    resizable: true
  },
  {
    label: 'Actions',
    key: '_actions',
    minWidth: 80,
    sortable: false,
    align: 'center',
    render: (_val, row) => (
      <Button
        size="small"
        variant="outlined"
        onClick={(e) => {
          e.stopPropagation();
          _editRowHandler?.(row);
        }}
        sx={{
          fontSize: '0.7rem',
          textTransform: 'none',
          borderRadius: 1.5,
          py: 0.3,
          px: 1.2,
          borderColor: 'divider',
          color: 'text.secondary',
          '&:hover': { borderColor: 'primary.main', color: 'primary.main' }
        }}
      >
        Edit
      </Button>
    )
  }
];

const monthOptions = [
  { label: 'All', value: 0 },
  { label: 'January', value: 1 },
  { label: 'February', value: 2 },
  { label: 'March', value: 3 },
  { label: 'April', value: 4 },
  { label: 'May', value: 5 },
  { label: 'June', value: 6 },
  { label: 'July', value: 7 },
  { label: 'August', value: 8 },
  { label: 'September', value: 9 },
  { label: 'October', value: 10 },
  { label: 'November', value: 11 },
  { label: 'December', value: 12 }
];

let _editRowHandler = null;

const initialFiltersValue = {
  dateFrom: dayjs('2015-01-01'),
  dateTo: dayjs(),
  clientName: '',
  clientGUID: 0,
  userGUID: 0,
  amcEndMonth: 0,
  amcStatusGUID: 2,
  statusGUID: 1,
  areaGUID: 0,
  cityGUID: 0,
  orgTypeGUID: 0
};

/**
 * InstallationDetailsList Component with Lazy Loading
 *
 * Features:
 * - Infinite scroll with pagination (50 records per batch)
 * - Filters: From/To Date, InCharge, AMC Month, AMC/Non AMC, Status, Client Name, Area, City, Org Type
 * - All dropdown options are dynamic
 * - Manual fetch: Click "Fetch" button to load data with current filters
 * - Add/Edit installation dialogs
 * - Manual refresh capability
 */
const InstallationDetailsList = () => {
  // Filters state (for UI inputs)
  const [filters, setFilters] = useState(initialFiltersValue);

  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedInstallation, setSelectedInstallation] = useState(null);

  // Filter options
  const [inChargeOptions, setInChargeOptions] = useState([]);

  const [clients, setClients] = useState([]);
  const [areas, setAreas] = useState([]);
  const [cities, setCities] = useState([]);
  const [orgTypes, setOrgTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [installations, setInstallations] = useState([]);
  const { isMobile, isTablet, isDesktop } = useResponsive();

  const user = localStorage.getItem(localStorageKeyName);
  const userDetails = user ? JSON.parse(user) : null;
  // const isUser = userDetails.role == AuthConstant.USER;
  const userAccess = userScreenAccess();

  const isAdmin = IsAdmins();

  // Wire module-level handler for the Actions column
  _editRowHandler = (row) => {
    setSelectedInstallation(row);
    setOpenDialog(true);
  };

  const handleAddClient = () => {
    if (!userAccess?.hasAddPermission) return;
    setSelectedInstallation(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedInstallation(null);
    fetchInstallationListResponse(filters);
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const handleStartDateRangeChange = (nextRange) => {
    setFilters({
      ...filters,
      dateFrom: nextRange ? dayjs(nextRange) : null
    });
  };

  const handleEndDateRangeChange = (nextRange) => {
    setFilters({
      ...filters,
      dateTo: nextRange ? dayjs(nextRange) : null
    });
  };

  const fetchInstallationListResponse = async (filters) => {
    setIsLoading(true);
    try {
      // Convert dayjs dates to YYYY-MM-DD string format to avoid timezone issues
      const payload = {
        ...filters,
        dateFrom: formatDate(filters.dateFrom),
        dateTo: formatDate(filters.dateTo)
      };
      const response = await fetchInstallations(payload);
      setInstallations(response || []);
    } catch (err) {
      console.log('Error fetching installation list:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDependencies = async () => {
    try {
      const [clientRes, usersRes, areasRes, citiesRes, orgTypes] = await Promise.all([
        fetchClients({ statusGUID: 1 }),
        fetchUsersCombo(),
        fetchAreaListCombo(),
        fetchCityListCombo(),
        fetchGlobalTypesCombo(MastersId.ORGANIZATION_TYPE)
      ]);

      const loadedClients = clientRes || [];
      const loadedUsers = usersRes || [];
      const loadedAreas = areasRes || [];
      const loadedCities = citiesRes || [];
      const loadedOrgType = orgTypes || [];

      setClients(loadedClients);
      setInChargeOptions(loadedUsers);
      setAreas(loadedAreas);
      setCities(loadedCities);
      setOrgTypes(loadedOrgType);
    } catch (err) {
      console.log('Failed to fetch dependencies:', err);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleFetch();
    }
  };

  // Fetch dependencies on mount
  React.useEffect(() => {
    fetchDependencies();
    const payload = {
      ...filters,
      userGUID: userDetails.userGUID ?? 0
    };
    setFilters(payload);
    fetchInstallationListResponse(payload);
  }, []);

  const handleCityChange = (event, value) => {
    const cityGUID = value?.cityGUID || 0;
    const next = { ...filters, cityGUID };
    setFilters(next);
  };

  const handleAreaChange = (event, value) => {
    const areaGUID = value?.areaGUID || 0;
    const next = { ...filters, areaGUID };
    setFilters(next);
  };

  const handleInChargeChange = (event, value) => {
    const userGUID = value?.userGUID || 0;
    const next = { ...filters, userGUID };
    setFilters(next);
  };

  const handleStatusChange = (event, value) => {
    const statusGUID = value?.value || 0;
    const next = { ...filters, statusGUID };
    setFilters(next);
  };

  const handleOrgTypeChange = (event, value) => {
    const orgTypeGUID = value?.globalTypeID || 0;
    const next = { ...filters, orgTypeGUID };
    setFilters(next);
  };

  // Filtered data
  const filteredData = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return installations;
    return installations.filter((row) => {
      // Normalize values: convert boolean isActive → display text so
      // searching "Active" or "Inactive" / "InActive" works correctly
      const normalizedValues = Object.entries(row).map(([key, val]) => {
        if (key === 'isActive')
          return typeof val === 'boolean'
            ? val
              ? 'active'
              : 'inactive'
            : String(val).toLowerCase();
        return String(val).toLowerCase();
      });
      return normalizedValues.some((v) => v.includes(q));
    });
  }, [installations, searchQuery]);

  // Calculate totals
  const totalBillAmount = useMemo(() => {
    return filteredData.reduce((sum, item) => sum + (item.billAmount || 0), 0);
  }, [filteredData]);

  const totalAmcAmount = useMemo(() => {
    return filteredData.reduce((sum, item) => sum + (item.amcAmount || 0), 0);
  }, [filteredData]);

  /**
   * NEW: Reset infinite scroll when filters change
   * This forces the DataTable to reinitialize with new filters
   */
  const handleResetFilters = () => {
    const payload = {
      ...initialFiltersValue,
      userGUID: userDetails.userGUID
    };
    setFilters(payload);
    fetchInstallationListResponse(payload);
  };

  const handleFetch = () => {
    // setApiFilters({ ...filters }); // Copy current UI filters to API filters
    // setFilterResetKey((prev) => prev + 1); // Trigger DataTable reset and fetch
    fetchInstallationListResponse(filters);
  };

  const handleRefresh = () => {
    fetchInstallationListResponse(filters);
  };

  const handleClientChange = (clientGUID) => {
    const next = { ...filters, clientGUID };
    setFilters(next);
  };

  return (
    <div className="w-100">
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          alignItems: 'center',
          justifyContent: { xs: 'space-between', md: 'flex-end' },
          mb: 3
        }}
      >
        <Typography
          variant="h6"
          fontWeight="bold"
          color="text.primary"
          sx={{ display: { md: 'none' } }}
        >
          Installation Details
        </Typography>
      </Box>

      <Box
        sx={{
          mb: 3,
          p: 2,
          backgroundColor: '#fff',
          borderRadius: 2,
          boxShadow: '0px 2px 10px rgba(0,0,0,0.04)',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        {/* Filter Fields Grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(5, 1fr)',
              lg: 'repeat(5, 1fr)'
            },
            gap: 1,
            mb: 2,
            width: '100%'
          }}
        >
          {/* Date Range Filter */}
          <Box
            sx={{
              width: '100%',
              minWidth: 0,
              gridColumn: { xs: 'span 1', sm: 'span 2', md: 'span 2', lg: 'span 2' }
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 1,
                minWidth: 0
              }}
            >
              <Box sx={{ width: '100%', minWidth: 0 }}>
                <DatePicker
                  placeholder="Start Date"
                  label="Start Date"
                  value={filters.dateFrom || null}
                  onChange={handleStartDateRangeChange}
                  height={36}
                  width="100%"
                  onKeyDown={handleKeyDown}
                />
              </Box>

              <Box sx={{ width: '100%', minWidth: 0 }}>
                <DatePicker
                  label="End Date"
                  placeholder="End Date"
                  value={filters.dateTo || null}
                  onChange={handleEndDateRangeChange}
                  height={36}
                  width="100%"
                  onKeyDown={handleKeyDown}
                />
              </Box>
            </Box>
          </Box>

          {/* AMC End Month Filter */}
          <TextField
            fullWidth
            size="small"
            select
            label="AMC End Month"
            name="amcEndMonth"
            value={filters.amcEndMonth}
            onChange={handleFilterChange}
            sx={FilterFieldSx}
            onKeyDown={handleKeyDown}
          >
            {monthOptions.map((month) => (
              <MenuItem key={month.value} value={month.value} sx={DropDownSX}>
                {month.label}
              </MenuItem>
            ))}
          </TextField>

          {/* AMC/Non AMC Filter */}
          <TextField
            fullWidth
            size="small"
            select
            label="AMC/Non AMC"
            name="amcStatusGUID"
            value={filters.amcStatusGUID}
            onChange={handleFilterChange}
            sx={FilterFieldSx}
            onKeyDown={handleKeyDown}
          >
            <MenuItem value={0} sx={DropDownSX}>
              All
            </MenuItem>
            <MenuItem value={1} sx={DropDownSX}>
              AMC
            </MenuItem>
            <MenuItem value={2} sx={DropDownSX}>
              Non-AMC
            </MenuItem>
          </TextField>

          <Autocomplete
            size="small"
            fullWidth
            options={ClientStatusOptions}
            value={ClientStatusOptions.find((u) => u.value === filters.statusGUID) || null}
            onChange={handleStatusChange}
            disablePortal={false}
            disableClearable={false}
            getOptionLabel={(option) => option?.label || ''}
            isOptionEqualToValue={(option, value) => option?.value === value?.value}
            onKeyDown={handleKeyDown}
            sx={DropDownSXWithColor}
            slotProps={{
              paper: {
                sx: DropDownSX
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Status"
                placeholder="Select Status"
                sx={FilterFieldSx}
              />
            )}
          />

          {/* Client Name */}
          <ChooseClient
            clients={clients}
            value={filters.clientGUID}
            onChange={handleClientChange}
            onKeyDown={handleKeyDown}
            customDropDownSX={{
              gridColumn: { xs: 'span 1', sm: 'span 2', md: 'span 2', lg: 'span 2' }
            }}
          />

          <Autocomplete
            size="small"
            fullWidth
            options={areas}
            value={areas.find((a) => a.areaGUID === filters.areaGUID) || null}
            onChange={handleAreaChange}
            disablePortal={false}
            disableClearable={false}
            getOptionLabel={(option) => option?.area || ''}
            isOptionEqualToValue={(option, value) => option?.areaGUID === value?.areaGUID}
            onKeyDown={handleKeyDown}
            sx={DropDownSXWithColor}
            slotProps={{
              paper: {
                sx: DropDownSX
              }
            }}
            renderInput={(params) => (
              <TextField {...params} label="Area" placeholder="Select Area" sx={FilterFieldSx} />
            )}
          />

          <Autocomplete
            size="small"
            fullWidth
            options={cities}
            value={cities.find((c) => c.cityGUID === filters.cityGUID) || null}
            onChange={handleCityChange}
            disablePortal={false}
            disableClearable={false}
            getOptionLabel={(option) => option?.city || ''}
            isOptionEqualToValue={(option, value) => option?.cityGUID === value?.cityGUID}
            onKeyDown={handleKeyDown}
            sx={DropDownSXWithColor}
            slotProps={{
              paper: {
                sx: DropDownSX
              }
            }}
            renderInput={(params) => (
              <TextField {...params} label="City" placeholder="Select City" sx={FilterFieldSx} />
            )}
          />

          <Autocomplete
            size="small"
            fullWidth
            options={inChargeOptions}
            value={inChargeOptions.find((u) => u.userGUID === filters.userGUID) || null}
            onChange={handleInChargeChange}
            disablePortal={false}
            disableClearable={false}
            getOptionLabel={(option) => option?.userName || ''}
            isOptionEqualToValue={(option, value) => option?.userGUID === value?.userGUID}
            onKeyDown={handleKeyDown}
            readOnly={!isAdmin}
            sx={DropDownSXWithColor}
            slotProps={{
              paper: {
                sx: DropDownSX
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="In Charge"
                placeholder="Select In Charge"
                sx={FilterFieldSx}
              />
            )}
          />

          {/* <Autocomplete
            size="small"
            fullWidth
            options={orgTypes}
            value={orgTypes.find((o) => o.globalTypeID === filters.orgTypeGUID) || null}
            onChange={handleOrgTypeChange}
            disablePortal={false}
            getOptionLabel={(option) => option?.globalTypeName || ''}
            isOptionEqualToValue={(option, value) => option?.globalTypeID === value?.globalTypeID}
            onKeyDown={handleKeyDown}
            slotProps={{
              paper: {
                sx: DropDownSX
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Organization Type"
                placeholder="Select Organization Type"
                sx={FilterFieldSx}
              />
            )}
          /> */}
        </Box>

        {/* Action Buttons Row */}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}
        >
          {/* Fetch Button */}
          <Tooltip text="Fetch">
            <Button
              variant="outlined"
              size="small"
              onClick={handleFetch}
              sx={{
                textTransform: 'none',
                borderRadius: 1,
                whiteSpace: 'nowrap',
                textCapitalize: 'capitalize',
                fontWeight: 500,
                fontSize: '0.8rem',
                py: 0.4,
                px: 1.2,
                color: CSS_PRIMARY, // text color
                borderColor: CSS_PRIMARY, // outline color

                '&:hover': {
                  borderColor: PRIMARY_COLOR_HOVER,
                  color: PRIMARY_COLOR_HOVER
                }
              }}
            >
              <SearchIcon size={14} style={{ marginRight: 4 }} />
              Fetch
            </Button>
          </Tooltip>

          {/* Refresh Button */}
          {/* <Tooltip text="Refresh">
            <Button
              variant="outlined"
              size="small"
              onClick={handleRefresh}
              sx={{
                textTransform: 'none',
                borderRadius: 1,
                whiteSpace: 'nowrap',
                fontWeight: 500,
                fontSize: '0.8rem',
                py: 0.4,
                px: 1.2
              }}
            >
              <RefreshCw size={14} style={{ marginRight: 4 }} />
              Refresh
            </Button>
          </Tooltip> */}

          {/* Reset Button */}
          <Tooltip text="Reset filters">
            <Button
              variant="outlined"
              size="small"
              onClick={handleResetFilters}
              sx={{
                textTransform: 'none',
                borderRadius: 1,
                whiteSpace: 'nowrap',
                fontWeight: 500,
                fontSize: '0.8rem',
                py: 0.4,
                px: 1.2,
                color: CSS_PRIMARY, // text color
                borderColor: CSS_PRIMARY, // outline color

                '&:hover': {
                  borderColor: PRIMARY_COLOR_HOVER,
                  color: PRIMARY_COLOR_HOVER
                }
              }}
            >
              <BrushCleaning size={14} style={{ marginRight: 4 }} />
              Reset
            </Button>
          </Tooltip>
        </Box>
      </Box>

      <div className="d-flex align-items-center justify-content-between mb-2 gap-2 flex-wrap">
        <Search
          width={isMobile ? '170px' : '280px'}
          height="36px"
          placeholder="Search by client name, area, city or in charge..."
          debounce={250}
          onSearch={setSearchQuery}
          onClear={() => setSearchQuery('')}
        />

        <div
          className={`align-items-center gap-3 flex-shrink-0 ${isDesktop ? 'd-flex' : 'd-none'}`}
        >
          {/* <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Total Bill: ₹{totalBillAmount.toLocaleString()}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Total AMC AMT: ₹{totalAmcAmount.toLocaleString()}
          </Typography> */}

          <Button
            variant="contained"
            size="small"
            sx={{
              textTransform: 'capitalize',
              backgroundColor: CSS_PRIMARY,
              '&:hover': {
                backgroundColor: PRIMARY_COLOR_HOVER
              }
            }}
          >
            Total Bill AMT: ₹{totalBillAmount.toLocaleString()}
          </Button>
          <Button
            variant="contained"
            size="small"
            sx={{
              textTransform: 'capitalize',
              backgroundColor: CSS_PRIMARY,
              '&:hover': {
                backgroundColor: PRIMARY_COLOR_HOVER
              }
            }}
          >
            Total AMC AMT: ₹{totalAmcAmount.toLocaleString()}
          </Button>
        </div>

        <div className="d-flex align-items-center gap-2 flex-shrink-0">
          <ExportExcel
            data={installations}
            fileName={`InstallationDetails_${new Date().toISOString().slice(0, 10)}.xlsx`}
            sheetName={'InstallationDetails'}
            buttonLabel="Excel"
            exportKeys={headers.filter((h) => h.key !== '_actions').map((h) => h.key)}
            exportHeaders={headers.filter((h) => h.key !== '_actions').map((h) => h.label)}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={<Plus size={15} />}
            onClick={handleAddClient}
            sx={{
              background: 'var(--primary, #82CAFF)',
              '&:hover': { background: 'var(--primary-dark, #27ae60)' },
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: 2,
              py: 0.8,
              fontSize: '0.83rem',
              boxShadow: '0 4px 12px rgba(46,204,113,0.28)',
              display: userAccess?.hasAddPermission ? 'inline-flex' : 'none'
            }}
          >
            Add
          </Button>
        </div>
      </div>

      {!isDesktop && (
        <div className="d-flex align-items-center gap-3 flex-shrink-0 mb-2 w-100 justify-content-center">
          <Button
            variant="contained"
            size="small"
            color="primary"
            sx={{ textTransform: 'capitalize' }}
          >
            Total Bill AMT: ₹{totalBillAmount.toLocaleString()}
          </Button>
          <Button
            variant="contained"
            size="small"
            color="primary"
            sx={{ textTransform: 'capitalize' }}
          >
            Total AMC AMT: ₹{totalAmcAmount.toLocaleString()}
          </Button>
        </div>
      )}

      {/* ── DataTable with Infinite Scroll ─────────────────────────────── */}
      {/* 
        Key points:
        1. key={filterResetKey} - Forces component reset when Fetch button clicked
        2. enableInfiniteScroll={true} - Enables lazy loading
        3. apiFunction={...} - Called to fetch next batch with apiFilters
        4. scrollHeight="600px" - Fixed height for scroll container
        5. enablePagination={false} - Disable traditional pagination
        6. Filters change UI but don't trigger fetch until Fetch button clicked
      */}
      {/* <DataTable
        key={filterResetKey}
        headers={headers}
        data={[]} // Empty initially; API will populate
        enableInfiniteScroll={true}
        apiFunction={fetchInstallationListWithPagination}
        pageSize={50}
        scrollHeight="400px"
        enablePagination={false}
        emptyMessage="No installation details found. Try adjusting your filters."
      /> */}

      <DataTable
        headers={headers}
        data={filteredData}
        isLoading={isLoading}
        selectable={false}
        emptyMessage={'No clients found in the system.'}
        enablePagination={true}
        zebra={false}
        onRowClick={_editRowHandler}
      />

      <CommonDialog
        open={openDialog}
        onClose={handleCloseDialog}
        title={selectedInstallation ? 'Edit Installation' : 'Add New Installation'}
        persistent={true}
        fullScreen={selectedInstallation}
        maxWidth={'lg'}
      >
        <AddInstallation
          userDetails={userDetails}
          clientsOptions={clients}
          installation={selectedInstallation}
          inChargeOptions={inChargeOptions}
          userAccess={userAccess}
          onSuccess={() => {
            handleCloseDialog();
            handleFetch(); // Refresh table after save
          }}
          onCancel={() => {
            handleCloseDialog();
            handleFetch(); // Refresh table after save
          }}
        />
      </CommonDialog>
    </div>
  );
};

export default InstallationDetailsList;
